import React, { useEffect, useState } from "react";
import { Loader2, Plus, X, Check, User, BadgePercent, BadgeDollarSign, PackagePlus } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";

const DISCOUNT_TIERS = [
  { amount: 2, points: 1000 },
  { amount: 5, points: 2500 },
  { amount: 10, points: 5000 },
  { amount: 20, points: 10000 },
];

export default function POSTerminal() {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [searching, setSearching] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState("");

  // member state
  const [member, setMember] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [tradeCredit, setTradeCredit] = useState(0); // dollars applied at checkout
  const [showAccountPrompt, setShowAccountPrompt] = useState(true);
  const [promptMode, setPromptMode] = useState("lookup"); // 'lookup' | 'create'
  const [promptInput, setPromptInput] = useState("");
  const [promptError, setPromptError] = useState("");
  const [memberLookup, setMemberLookup] = useState("");
  const [memberLookupErr, setMemberLookupErr] = useState("");
  const [newMember, setNewMember] = useState({ first_name: "", last_name: "", birth_month: 1, phone: "", email: "" });
  const [creating, setCreating] = useState(false);

  // ad-hoc (non-inventory) line modal
  const [showAdHoc, setShowAdHoc] = useState(false);
  const [adHocTitle, setAdHocTitle] = useState("");
  const [adHocPrice, setAdHocPrice] = useState("");
  const [adHocErr, setAdHocErr] = useState("");

  const navigate = useNavigate();

  const subtotal = cart.reduce((s, it) => s + Number(it.sale_price), 0);
  // Bundle discount: every 3 GAME items priced at $2 → $1 off (3-for-$5 promo).
  // Non-game AND ad-hoc items are excluded from the promo.
  const twoDollarCount = cart.filter((it) => Number(it.sale_price) === 2 && !it.is_non_game && !it.is_ad_hoc).length;
  const bundleDiscount = Math.floor(twoDollarCount / 3);
  const afterDiscount = Math.max(subtotal - discount - bundleDiscount, 0);
  const total = Math.max(afterDiscount - tradeCredit, 0);
  const pointsEarn = member ? Math.floor(afterDiscount * 25) : 0;

  const resetSale = () => {
    setCart([]); setMember(null); setDiscount(0); setTradeCredit(0);
    setShowAccountPrompt(true); setPromptMode("lookup"); setPromptInput(""); setPromptError("");
    setMemberLookup(""); setMemberLookupErr("");
    setNewMember({ first_name: "", last_name: "", birth_month: 1, phone: "", email: "" });
  };

  const createMember = async (e) => {
    e?.preventDefault();
    setPromptError(""); setCreating(true);
    try {
      const payload = { ...newMember, birth_month: Number(newMember.birth_month) };
      const r = await api.post("/admin/members", payload);
      setMember(r.data);
      setDiscount(0);
      setShowAccountPrompt(false);
      setPromptMode("lookup");
      setPromptInput("");
      setNewMember({ first_name: "", last_name: "", birth_month: 1, phone: "", email: "" });
    } catch (err) {
      setPromptError(err.response?.data?.detail || "Failed to create member");
    } finally {
      setCreating(false);
    }
  };

  const lookupMember = async (phone, source) => {
    setMsg("");
    try {
      const r = await api.get(`/admin/members/${encodeURIComponent(phone.trim())}`);
      setMember(r.data);
      setDiscount(0);
      if (source === "prompt") {
        setShowAccountPrompt(false);
        setPromptError("");
        setPromptInput("");
      } else {
        setMemberLookup("");
        setMemberLookupErr("");
      }
    } catch (err) {
      const message = err.response?.status === 404 ? "No account found." : (err.response?.data?.detail || "Lookup failed");
      if (source === "prompt") setPromptError(message);
      else setMemberLookupErr(message);
    }
  };

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    try {
      const r = await api.get("/pos/search", { params: { q: search.trim() } });
      setResults(r.data.results);
      const q = search.trim();
      const oneBarcodeHit =
        r.data.results.length === 1 &&
        (r.data.results[0].barcode === q ||
          (Array.isArray(r.data.results[0].barcodes) &&
            r.data.results[0].barcodes.includes(q)));
      if (oneBarcodeHit) {
        addToCart(r.data.results[0]);
        setResults([]); setSearch("");
      }
    } finally {
      setSearching(false);
    }
  };

  const addToCart = (item) => { setCart((p) => [...p, item]); setResults([]); setSearch(""); };
  const removeFromCart = (idx) => setCart((p) => p.filter((_, i) => i !== idx));

  const openAdHoc = () => {
    setAdHocTitle(""); setAdHocPrice(""); setAdHocErr(""); setShowAdHoc(true);
  };
  const addAdHoc = (e) => {
    e?.preventDefault();
    const price = Number(adHocPrice);
    if (!Number.isFinite(price) || price < 0) {
      setAdHocErr("Enter a valid price (≥ 0).");
      return;
    }
    setCart((p) => [...p, {
      id: `__ad_hoc__${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      title: adHocTitle.trim() || "Non-Inventory Item",
      console: "Non-Inventory",
      sale_price: Math.round(price * 100) / 100,
      is_ad_hoc: true,
    }]);
    setShowAdHoc(false);
  };

  // Auto-clamp discount/trade credit if cart shrinks
  useEffect(() => {
    if (discount > 0 && subtotal < discount) setDiscount(0);
  }, [subtotal, discount]);
  useEffect(() => {
    const remaining = Math.max(subtotal - discount - bundleDiscount, 0);
    if (tradeCredit > remaining) setTradeCredit(remaining);
    const memberCap = member ? Number(member.trade_credit || 0) : 0;
    if (tradeCredit > memberCap) setTradeCredit(memberCap);
  }, [subtotal, discount, bundleDiscount, tradeCredit, member]);

  const confirmSale = async () => {
    if (cart.length === 0) return;
    setConfirming(true);
    try {
      const body = {
        inventory_ids: cart.filter((it) => !it.is_ad_hoc).map((it) => it.id),
        ad_hoc_items: cart
          .filter((it) => it.is_ad_hoc)
          .map((it) => ({ title: it.title, price: Number(it.sale_price) })),
        member_phone: member?.phone || null,
        discount_amount: discount || 0,
        trade_credit_amount: tradeCredit || 0,
      };
      const r = await api.post("/pos/sale", body);
      const pieces = [`Sale complete. Total: $${r.data.total.toFixed(2)}`];
      if (r.data.points_earned) pieces.push(`+${r.data.points_earned} µPoints earned`);
      if (r.data.points_redeemed) pieces.push(`-${r.data.points_redeemed} µPoints redeemed`);
      if (r.data.trade_credit_used) pieces.push(`-$${r.data.trade_credit_used} trade credit`);
      setMsg(pieces.join(" · "));
      resetSale();
    } catch (err) {
      setMsg(err.response?.data?.detail || "Failed to confirm sale.");
    } finally {
      setConfirming(false);
    }
  };

  const cancelSale = () => {
    resetSale();
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <button onClick={() => navigate("/pos/dashboard")} className="label-eyebrow inline-block" data-testid="pos-terminal-back">← Back to Dashboard</button>
          <div className="flex gap-2">
            <button className="btn-primary inline-flex items-center gap-2" disabled data-testid="pos-active">POS Terminal</button>
            <button onClick={() => navigate("/pos/trade")} className="btn-secondary inline-flex items-center gap-2" data-testid="pos-toggle-trade"><BadgeDollarSign size={14} /> Trade Terminal</button>
          </div>
        </div>
        <h1 className="font-display text-4xl font-black mb-8">POS Terminal</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT: product search & results only */}
          <div className="lg:col-span-2 space-y-3">
            <form onSubmit={doSearch} className="surface flex items-stretch">
              <input
                className="flex-1 bg-transparent border-0 px-4 py-4 outline-none font-mono text-lg"
                placeholder="Scan barcode or type title…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                data-testid="pos-terminal-search"
              />
              <button type="submit" className="btn-primary px-6" data-testid="pos-terminal-search-btn">
                {searching ? <Loader2 size={16} className="animate-spin" /> : "Find"}
              </button>
            </form>

            <button
              type="button"
              onClick={openAdHoc}
              className="btn-secondary w-full inline-flex items-center justify-center gap-2"
              data-testid="pos-add-adhoc"
            >
              <PackagePlus size={14} /> Add Non-Inventory Item
            </button>

            {results.length > 0 && (
              <div className="surface divide-y" style={{ borderColor: "var(--ab-border)" }}>
                {results.map((it) => {
                  // Compact condition/components label so cashier can tell
                  // CIB vs Loose vs New variants apart when a single barcode
                  // resolves to multiple rows (e.g. Pokemon Blue CIB + Loose).
                  const variantBits = [];
                  if (it.components === "Game in Box with Manual") variantBits.push("CIB");
                  else if (it.components === "Game with missing Components") variantBits.push("Loose");
                  else if (it.condition === "New") variantBits.push("New (sealed)");
                  else if (it.components) variantBits.push(it.components);
                  if (it.condition && it.condition !== "New") variantBits.push(it.condition);
                  const variant = variantBits.join(" · ");
                  return (
                  <div key={it.id} className="flex justify-between items-center px-4 py-3" data-testid="pos-search-result">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate">{it.title}</div>
                      <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>
                        {it.console} &middot; ${Number(it.sale_price).toFixed(0)}
                        {variant && <> &middot; <span style={{ color: "var(--ab-primary)" }}>{variant}</span></>}
                      </div>
                    </div>
                    <button onClick={() => addToCart(it)} className="btn-primary" style={{ padding: "8px 14px", fontSize: 11 }} data-testid={`pos-add-${it.id}`}>
                      <Plus size={14} />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}

            {msg && <div className="surface p-4 text-sm" data-testid="pos-msg">{msg}</div>}
          </div>

          {/* RIGHT: member panel ABOVE current sale */}
          <div className="space-y-3">
            {/* Member panel */}
            <div className="surface p-5" data-testid="pos-member-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow inline-flex items-center gap-2" style={{ color: "var(--ab-primary)" }}>
                  <BadgePercent size={12} /> AB Member
                </div>
                {member && <button onClick={() => { setMember(null); setDiscount(0); setTradeCredit(0); }} className="text-xs label-eyebrow hover:text-[var(--ab-danger)]" data-testid="pos-member-clear">Remove</button>}
              </div>

              {!member ? (
                <form onSubmit={(e) => { e.preventDefault(); if (memberLookup.trim()) lookupMember(memberLookup, "side"); }} className="flex items-stretch gap-2">
                  <input className="input-terminal flex-1" placeholder="Account # (phone)…" value={memberLookup} onChange={(e) => setMemberLookup(e.target.value)} data-testid="pos-member-input" />
                  <button type="submit" className="btn-secondary" data-testid="pos-member-attach">Attach</button>
                </form>
              ) : (
                <div>
                  <div className="font-display text-xl font-bold flex items-center gap-2"><User size={18} /> Welcome, {member.first_name}</div>
                  <div className="font-mono text-xs mt-1" style={{ color: "var(--ab-text-muted)" }}>
                    Balance: <b style={{ color: "var(--ab-primary)" }}>{Number(member.points).toLocaleString()}</b> µPoints
                  </div>
                  <div className="font-mono text-xs mt-1" style={{ color: "var(--ab-text-muted)" }} data-testid="pos-member-trade-credit">
                    Trade Credit: <b style={{ color: "var(--ab-primary)" }}>${Number(member.trade_credit || 0).toLocaleString()}</b>
                  </div>
                  {pointsEarn > 0 && (
                    <div className="font-mono text-xs mt-1" style={{ color: "var(--ab-text-muted)" }}>
                      Earning <b style={{ color: "var(--ab-primary)" }}>+{pointsEarn.toLocaleString()}</b> this sale
                    </div>
                  )}
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {DISCOUNT_TIERS.map((t) => {
                      const canPoints = (member.points || 0) >= t.points;
                      const canSubtotal = subtotal >= t.amount;
                      const enabled = canPoints && canSubtotal;
                      const active = discount === t.amount;
                      return (
                        <button
                          key={t.amount}
                          type="button"
                          disabled={!enabled}
                          onClick={() => setDiscount(active ? 0 : t.amount)}
                          className="border px-3 py-2 text-center transition-colors"
                          style={{
                            borderColor: active ? "var(--ab-primary)" : "var(--ab-border)",
                            background: active ? "rgba(59,130,246,0.12)" : "transparent",
                            color: enabled ? "var(--ab-text)" : "var(--ab-text-dim)",
                            cursor: enabled ? "pointer" : "not-allowed",
                            opacity: enabled ? 1 : 0.4,
                          }}
                          data-testid={`pos-discount-${t.amount}`}
                        >
                          <div className="font-mono font-bold" style={{ color: enabled ? "var(--ab-primary)" : "inherit" }}>${t.amount}</div>
                          <div className="text-[10px]" style={{ color: "var(--ab-text-dim)" }}>({t.points.toLocaleString()} pts)</div>
                        </button>
                      );
                    })}
                  </div>
                  {/* Trade-credit input */}
                  {Number(member.trade_credit || 0) > 0 && (
                    <div className="mt-4">
                      <label className="label-eyebrow block mb-2">Apply Trade Credit ($)</label>
                      <div className="flex items-stretch gap-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          max={Math.min(Number(member.trade_credit || 0), Math.max(subtotal - discount - bundleDiscount, 0))}
                          value={tradeCredit || ""}
                          onChange={(e) => {
                            const cap = Math.min(Number(member.trade_credit || 0), Math.max(subtotal - discount - bundleDiscount, 0));
                            const v = Math.max(0, Math.min(cap, Math.floor(Number(e.target.value) || 0)));
                            setTradeCredit(v);
                          }}
                          className="input-terminal flex-1"
                          placeholder="0"
                          data-testid="pos-trade-credit-input"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const cap = Math.min(Number(member.trade_credit || 0), Math.max(subtotal - discount - bundleDiscount, 0));
                            setTradeCredit(cap);
                          }}
                          className="btn-secondary text-xs px-3"
                          data-testid="pos-trade-credit-max"
                        >Max</button>
                      </div>
                      <div className="text-[10px] mt-1" style={{ color: "var(--ab-text-dim)" }}>
                        Available: ${Number(member.trade_credit || 0).toLocaleString()} · Cap: ${Math.min(Number(member.trade_credit || 0), Math.max(subtotal - discount - bundleDiscount, 0)).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {memberLookupErr && <div className="mt-3 text-xs" style={{ color: "var(--ab-danger)" }} data-testid="pos-member-err">{memberLookupErr}</div>}
            </div>

            {/* Cart */}
            <div className="surface flex flex-col">
              <div className="px-5 py-4 border-b flex justify-between" style={{ borderColor: "var(--ab-border)" }}>
                <span className="label-eyebrow">Current Sale</span>
                <span className="label-eyebrow">{cart.length} item{cart.length === 1 ? "" : "s"}</span>
              </div>
              <div className="flex-1 max-h-[55vh] overflow-auto">
                {cart.length === 0 ? (
                  <div className="p-6 text-sm" style={{ color: "var(--ab-text-dim)" }}>No items.</div>
                ) : (
                  cart.map((it, idx) => (
                    <div key={idx} className="flex justify-between items-center px-5 py-3 border-b" style={{ borderColor: "var(--ab-border)" }} data-testid="pos-cart-row">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold truncate">
                          {it.title}
                          {it.is_ad_hoc && (
                            <span className="ml-2 inline-block text-[9px] px-1.5 py-0.5 font-mono" style={{ background: "rgba(245,158,11,0.15)", color: "var(--ab-warning)", border: "1px solid var(--ab-warning)" }} data-testid="pos-cart-adhoc-tag">NON-INV</span>
                          )}
                        </div>
                        <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.console}</div>
                      </div>
                      <div className="font-mono font-bold" style={{ color: "var(--ab-primary)" }}>${Number(it.sale_price).toFixed(it.is_ad_hoc || it.is_non_game ? 2 : 0)}</div>
                      <button onClick={() => removeFromCart(idx)} className="ml-3 inline-flex items-center justify-center" style={{ width: 28, height: 28, border: "1px solid var(--ab-danger)", color: "var(--ab-danger)", background: "rgba(239,68,68,0.08)" }} data-testid={`pos-cart-remove-${idx}`}><X size={14} /></button>
                    </div>
                  ))
                )}
              </div>
              <div className="px-5 py-3 border-t font-mono text-xs space-y-1" style={{ borderColor: "var(--ab-border)" }}>
                <div className="flex justify-between" style={{ color: "var(--ab-text-muted)" }}>
                  <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between" style={{ color: "var(--ab-warning)" }} data-testid="pos-discount-line">
                    <span>µPoint discount</span><span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                {bundleDiscount > 0 && (
                  <div className="flex justify-between" style={{ color: "var(--ab-primary)" }} data-testid="pos-bundle-line">
                    <span>Bundle discount (3-for-$5)</span><span>-${bundleDiscount.toFixed(2)}</span>
                  </div>
                )}
                {tradeCredit > 0 && (
                  <div className="flex justify-between" style={{ color: "var(--ab-primary)" }} data-testid="pos-trade-credit-line">
                    <span>Trade credit</span><span>-${tradeCredit.toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t flex justify-between items-center" style={{ borderColor: "var(--ab-border)", background: "var(--ab-surface-2)" }}>
                <span className="label-eyebrow">Total</span>
                <span className="font-mono text-2xl font-bold" style={{ color: "var(--ab-primary)" }} data-testid="pos-cart-total">${total.toFixed(2)}</span>
              </div>
              <div className="p-4 grid grid-cols-2 gap-2">
                <button onClick={cancelSale} disabled={cart.length === 0} className="btn-danger" data-testid="pos-cancel-sale">Cancel</button>
                <button onClick={confirmSale} disabled={cart.length === 0 || confirming} className="btn-primary inline-flex items-center justify-center gap-2" data-testid="pos-confirm-sale">
                  {confirming ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account prompt at start of each sale */}
      {showAccountPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} data-testid="pos-account-prompt">
          <div className="surface p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Step 1</div>
            <h3 className="font-display text-2xl font-bold mb-4">AB Member Account?</h3>

            {/* Tabs */}
            <div className="flex gap-1 mb-5" style={{ borderBottom: "1px solid var(--ab-border)" }}>
              <button
                type="button"
                onClick={() => { setPromptMode("lookup"); setPromptError(""); }}
                className="px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
                style={{
                  color: promptMode === "lookup" ? "var(--ab-primary)" : "var(--ab-text-dim)",
                  borderBottom: promptMode === "lookup" ? "2px solid var(--ab-primary)" : "2px solid transparent",
                  marginBottom: "-1px",
                }}
                data-testid="pos-tab-lookup"
              >
                Existing Member
              </button>
              <button
                type="button"
                onClick={() => { setPromptMode("create"); setPromptError(""); }}
                className="px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
                style={{
                  color: promptMode === "create" ? "var(--ab-primary)" : "var(--ab-text-dim)",
                  borderBottom: promptMode === "create" ? "2px solid var(--ab-primary)" : "2px solid transparent",
                  marginBottom: "-1px",
                }}
                data-testid="pos-tab-create"
              >
                New Member
              </button>
            </div>

            {promptMode === "lookup" ? (
              <form onSubmit={(e) => { e.preventDefault(); if (promptInput.trim()) lookupMember(promptInput, "prompt"); }}>
                <p className="text-sm mb-5" style={{ color: "var(--ab-text-muted)" }}>
                  Enter the customer's account number (phone). They'll earn 25 µPoints per $1 spent.
                </p>
                <input
                  className="input-terminal mb-3"
                  placeholder="555 123 4567"
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  autoFocus
                  data-testid="pos-account-input"
                />
                {promptError && <div className="text-sm mb-3" style={{ color: "var(--ab-danger)" }} data-testid="pos-account-error">{promptError}</div>}
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => { setShowAccountPrompt(false); setPromptError(""); }} className="btn-secondary" data-testid="pos-account-cancel">Skip</button>
                  <button type="submit" className="btn-primary" data-testid="pos-account-attach">Attach</button>
                </div>
              </form>
            ) : (
              <form onSubmit={createMember}>
                <p className="text-sm mb-4" style={{ color: "var(--ab-text-muted)" }}>
                  Sign the customer up now — their account will attach to this sale automatically.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input className="input-terminal" placeholder="First name" required value={newMember.first_name} onChange={(e) => setNewMember(p => ({...p, first_name: e.target.value}))} data-testid="pos-new-first" />
                  <input className="input-terminal" placeholder="Last name" required value={newMember.last_name} onChange={(e) => setNewMember(p => ({...p, last_name: e.target.value}))} data-testid="pos-new-last" />
                </div>
                <input className="input-terminal mb-3" placeholder="Phone (account #)" required value={newMember.phone} onChange={(e) => setNewMember(p => ({...p, phone: e.target.value}))} data-testid="pos-new-phone" />
                <input className="input-terminal mb-3" type="email" placeholder="Email" required value={newMember.email} onChange={(e) => setNewMember(p => ({...p, email: e.target.value}))} data-testid="pos-new-email" />
                <select className="input-terminal mb-3" value={newMember.birth_month} onChange={(e) => setNewMember(p => ({...p, birth_month: e.target.value}))} data-testid="pos-new-birth">
                  {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                    <option key={i+1} value={i+1}>{m}</option>
                  ))}
                </select>
                {promptError && <div className="text-sm mb-3" style={{ color: "var(--ab-danger)" }} data-testid="pos-account-error">{promptError}</div>}
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => { setShowAccountPrompt(false); setPromptError(""); }} className="btn-secondary" disabled={creating} data-testid="pos-account-cancel">Skip</button>
                  <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={creating} data-testid="pos-new-create">
                    {creating ? <Loader2 size={14} className="animate-spin" /> : null} Create &amp; Attach
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {/* Ad-hoc / Non-Inventory Item modal */}
      {showAdHoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} data-testid="pos-adhoc-modal" onClick={() => setShowAdHoc(false)}>
          <form onSubmit={addAdHoc} className="surface p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-warning)" }}>Non-Inventory Item</div>
            <h3 className="font-display text-2xl font-bold mb-4">Add to Sale</h3>
            <p className="text-sm mb-4" style={{ color: "var(--ab-text-muted)" }}>
              One-off item that isn't tracked in inventory. Enter a price (required) and optional description.
            </p>
            <label className="label-eyebrow block mb-2">Description (optional)</label>
            <input
              className="input-terminal mb-4"
              placeholder="e.g. Controller cleaning, gift card top-up…"
              value={adHocTitle}
              onChange={(e) => setAdHocTitle(e.target.value)}
              data-testid="pos-adhoc-title"
            />
            <label className="label-eyebrow block mb-2">Price ($)</label>
            <input
              className="input-terminal mb-3"
              type="number"
              min="0"
              step="0.01"
              required
              autoFocus
              placeholder="0.00"
              value={adHocPrice}
              onChange={(e) => setAdHocPrice(e.target.value)}
              data-testid="pos-adhoc-price"
            />
            {adHocErr && <div className="text-sm mb-3" style={{ color: "var(--ab-danger)" }} data-testid="pos-adhoc-error">{adHocErr}</div>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAdHoc(false)} className="btn-secondary" data-testid="pos-adhoc-cancel">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="pos-adhoc-add">Add to Sale</button>
            </div>
          </form>
        </div>
      )}

    </Layout>
  );
}
